import { useCallback, useMemo, useState } from 'react'
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  styled,
  Typography,
} from '@mui/material'
import { Hall, HallType, InviteLink } from '@weblite/ts-action/hall'
import { REGEX_COMPILED } from '@weblite/permission'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { MenuIcon } from '$common/icons/menu'
import hostConf from '$config/frontend'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { Alert } from '$common/input/alert'
import { LinkInput } from '$common/input/link-input'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { useRoom } from '$logic/room/react-hook'
import {
  COURSE_VITRINE_TAG,
  ISSUE_TAG,
  useHall,
  useHallInvitationLinks,
} from '$logic/hall/react-hook'
import {
  closeDefineHallAccessibility,
  closeEditHallAccessibility,
  openAddHallMembers,
} from '$logic/router'
import { changeHallInfo, makeHallPrivate, makeHallPublic } from '$logic/hall'
import { hallConstraints } from '$logic/hall/constant'
import { t } from '$setup/internationalization'
import { showLinkMenu } from '$comp/modals/menus/link-menu'
import { vibrate } from '$func/vibrate'
import { RadioButtonListItem } from '$common/radio-button-list-item'
import { addTagToHallTags, removeTagFromHallTags } from '$logic/hall/helper'
import { ChannelType } from '$comp/_app/components/leftPanel/components/create-hall'
import { HallAdvancedSettingsOptions } from '$common/hall-advanced-settings-options'

type InvitationLinkStatus =
  | 'AVAILABLE'
  | 'NOT_AVAILABLE'
  | 'INVALID'
  | 'LESS_THAN_5_CHAR'
  | 'NOT_START_WITH_LETTER'

const i18nMap: Record<InvitationLinkStatus, string> = {
  AVAILABLE: t('validation.invitationLink.isAvailable'),
  INVALID: t('validation.invitationLink.notSupportedChars'),
  LESS_THAN_5_CHAR: t('validation.invitationLink.leastChars'),
  NOT_START_WITH_LETTER: t('validation.invitationLink.notStartWithLetter'),
  NOT_AVAILABLE: t('validation.invitationLink.exists'),
}

const LinkSection = styled(FormControl)`
  margin-top: 10px;
  padding: 10px 20px 20px;
  box-sizing: border-box;
  background-color: white;
`

const HallAccessibilitySection = styled(FormControl)`
  padding: 10px 20px;
  background-color: white;
`

const AdvancedSettingsSection = styled('div')`
  margin-top: 16px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  display: flex;
  flex-direction: column;
`

function SectionLabel({ text }: { text: string }) {
  return (
    <FormLabel>
      <Typography variant="subtitle3" color="secondary700">
        {text}
      </Typography>
    </FormLabel>
  )
}

interface Props {
  hall: Hall
  isHallCreation?: boolean
  hallType: HallType
  channelType?: ChannelType
  onBack: () => unknown
  onComplete: () => unknown
}

export function HallAccessibility({
  hall,
  isHallCreation,
  hallType,
  channelType,
  onBack,
  onComplete,
}: Props) {
  const defaultPublic = hall.public
  const defaultIdentifier = hall.identifier
  const defaultTags = hall.tags
  const defaultProtectContent = hall.protectContent

  const { roomId } = hall

  const [isPublic, setIsPublic] = useState(
    isHallCreation ? false : defaultPublic,
  )
  const [identifier, setIdentifier] = useState(defaultIdentifier || '')
  const [status, setStatus] = useState<InvitationLinkStatus>()

  const [tags, setTags] = useState<string[]>(defaultTags || [])

  const toggleTag = useCallback(
    (tag: string) => {
      if (tags.includes(tag)) setTags(removeTagFromHallTags(tag, tags || []))
      if (!tags.includes(tag)) setTags(addTagToHallTags(tag, tags || []))
    },
    [tags],
  )

  const isEducational = channelType === ChannelType.educational

  const vitrineEnabled = useMemo(() => {
    return tags.includes(COURSE_VITRINE_TAG)
  }, [tags])

  const issueEnabled = useMemo(() => {
    return tags.includes(ISSUE_TAG)
  }, [tags])

  const [protectContent, setProtectContent] = useState<boolean>(
    defaultProtectContent,
  )

  const alert = status ? i18nMap[status] : null
  const variant = status === 'AVAILABLE' ? 'success' : 'error'
  const isRepetitive = status === 'NOT_AVAILABLE'

  const { items } = useConditionalHook(!!isHallCreation, {
    hook: useHallInvitationLinks,
    hookArgs: roomId,
    defaultValue: undefined,
  }) || { items: [] as InviteLink[] }

  const invitationLink = items[0]?.link

  const handleConfirm = async () => {
    if (!isPublic) {
      await showSaveChangesLoadingModal()
      await makeHallPrivate(roomId)
      if (isEducational) await changeHallInfo(roomId, { tags, protectContent })
      await closeCurrentModal()

      onComplete()
      return
    }

    if (identifier) {
      if (identifier.length && !/[a-z]/i.test(identifier[0]))
        setStatus('NOT_START_WITH_LETTER')
      else if (identifier.length && identifier.length < 5)
        setStatus('LESS_THAN_5_CHAR')
      else if (identifier.length && !REGEX_COMPILED.username.test(identifier))
        setStatus('INVALID')
      else {
        setStatus(undefined)

        await showSaveChangesLoadingModal()
        const res = await makeHallPublic(roomId, identifier)
        if (isEducational)
          await changeHallInfo(roomId, { tags, protectContent })
        await closeCurrentModal()

        if (!res) {
          setStatus('NOT_AVAILABLE')
          vibrate()
        } else onComplete()
      }
    }
  }

  const openLinkMenu = useCallback(() => {
    showLinkMenu({
      link: `${hostConf.FRONT_HOST}/${isPublic ? identifier : invitationLink}`,
    })
  }, [identifier, invitationLink, isPublic])

  const canOpenLinkMenu =
    (!isPublic && invitationLink) ||
    (isPublic && defaultIdentifier && identifier === defaultIdentifier)

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={
            isEducational && hallType === 'CHANNEL'
              ? t('hallAccessibility.hallSetting.educationalChannel')
              : t(`hallAccessibility.hallSetting.${hallType.toLowerCase()}`)
          }
          onConfirm={handleConfirm}
          onBack={onBack}
          confirmDisabled={isPublic && !identifier}
        />
      }
    >
      <HallAccessibilitySection>
        <SectionLabel
          text={t(`hallAccessibility.type.${hallType.toLowerCase()}`)}
        />
        <RadioGroup
          defaultValue={defaultPublic ? 'public' : 'private'}
          name="radio-buttons-group"
        >
          {['private', 'public'].map(type => (
            <RadioButtonListItem
              onClick={() => setIsPublic(type === 'public')}
              value={type}
              key={type}
              primaryText={t(
                `hallAccessibility.access.${type}.${hallType.toLowerCase()}.title`,
              )}
              secondaryText={t(
                `hallAccessibility.access.${type}.${hallType.toLowerCase()}.description`,
              )}
            />
          ))}
        </RadioGroup>
      </HallAccessibilitySection>

      {(isPublic || isHallCreation) && (
        <LinkSection>
          <SectionLabel
            text={t(
              `hallAccessibility.link.${isPublic ? 'public' : 'private'}`,
            )}
          />

          <LinkInput
            sx={{ marginTop: '10px' }}
            identifier={isPublic ? identifier : invitationLink}
            isRepetitive={isRepetitive}
            handleChange={(newValue: string) => {
              setStatus(undefined)
              setIdentifier(newValue)
            }}
            readOnly={!isPublic}
            maxLength={hallConstraints.link.maxLength}
            endIcon={canOpenLinkMenu ? <MenuIcon /> : undefined}
            onEndIconClick={openLinkMenu}
          />

          {isPublic && (
            <>
              {alert && <Alert text={alert} variant={variant} />}
              <Box sx={{ marginTop: '8px' }}>
                {['legalCharacters', 'minimumLength'].map(rule => (
                  <Typography
                    variant="caption1"
                    color="gray"
                    display="inline-block"
                    key={rule}
                  >
                    {t(`hallAccessibility.publicLinkHint.${rule}`)}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </LinkSection>
      )}

      {isHallCreation && isEducational && (
        <AdvancedSettingsSection>
          <Typography
            margin="12px 20px 0 0"
            variant="subtitle3"
            color="secondary700"
          >
            {t(`createHall.advancedOptions`)}
          </Typography>
          <HallAdvancedSettingsOptions
            vitrineEnabled={vitrineEnabled}
            issueEnabled={issueEnabled}
            protectContent={protectContent}
            setProtectContent={setProtectContent}
            toggleTag={toggleTag}
          />
        </AdvancedSettingsSection>
      )}
    </PageWithAppBar>
  )
}

export function DefineHallAccessibility({
  roomId,
  channelType,
}: {
  roomId: string
  channelType: ChannelType
}) {
  const room = useRoom(roomId)
  const { entity: hall } = useHall(roomId)

  if (!room || !hall) return null

  return (
    <HallAccessibility
      hall={hall}
      isHallCreation
      hallType={room.type as HallType}
      channelType={channelType}
      onBack={closeDefineHallAccessibility}
      onComplete={() => openAddHallMembers(roomId, true, true)}
    />
  )
}

export function EditHallAccessibility({
  roomId,
  channelType,
}: {
  roomId: string
  channelType: ChannelType
}) {
  const { entity: hall } = useHall(roomId)
  const room = useRoom(roomId)

  if (!room || !hall) return null

  return (
    <HallAccessibility
      hall={hall}
      isHallCreation={false}
      hallType={room.type as HallType}
      channelType={channelType}
      onBack={closeEditHallAccessibility}
      onComplete={closeEditHallAccessibility}
    />
  )
}
